/**
 * Media bytes are bearer-authenticated (docs/CONTRACT.md §2/§4), so `<img src="/api/media/…">`
 * cannot work: every read goes through `fetchRaw` and becomes an object URL. Memoised by path,
 * with the same LRU as apps/web — the two caps are deliberately identical (M13), keep them in step.
 */
import { api } from './api'

const objectUrls = new Map<string, string>()
const inflight = new Map<string, Promise<string>>()
const MAX_ENTRIES = 120

function remember(path: string, url: string): void {
  objectUrls.set(path, url)
  if (objectUrls.size <= MAX_ENTRIES) return
  const oldest = objectUrls.keys().next()
  if (oldest.done) return
  const stale = objectUrls.get(oldest.value)
  objectUrls.delete(oldest.value)
  if (stale) URL.revokeObjectURL(stale)
}

export async function loadMediaUrl(path: string): Promise<string> {
  const cached = objectUrls.get(path)
  if (cached) return cached
  const pending = inflight.get(path)
  if (pending) return pending
  const request = (async () => {
    const response = await api.fetchRaw(path)
    if (!response.ok) throw new Error(`media request failed with ${response.status}`)
    const url = URL.createObjectURL(await response.blob())
    remember(path, url)
    return url
  })()
  inflight.set(path, request)
  try {
    return await request
  } finally {
    inflight.delete(path)
  }
}
